import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/hooks/useAuth";

import { ScrollToTop } from "@/components/ScrollToTop";

// Eagerly load only the Index page for fastest FCP
import Index from "./pages/Index";

// Retries a dynamic import once, then forces a one-time reload so stale
// chunk hashes from a previous deploy can't leave a blank screen.
function lazyRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
  key: string
) {
  return lazy(async () => {
    const flag = `chunk-reload:${key}`;
    try {
      const mod = await factory();
      sessionStorage.removeItem(flag);
      return mod;
    } catch (err) {
      if (!sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, "1");
        window.location.reload();
        return new Promise<T>(() => {});
      }
      throw err;
    }
  });
}

// Lazy-load all other routes for code splitting
const Portfolio = lazyRetry(() => import("./pages/Portfolio"), "./pages/Portfolio");
const Shop = lazyRetry(() => import("./pages/Shop"), "./pages/Shop");
const Blog = lazyRetry(() => import("./pages/Blog"), "./pages/Blog");
const BlogPost = lazyRetry(() => import("./pages/BlogPost"), "./pages/BlogPost");
const Pricing = lazyRetry(() => import("./pages/Pricing"), "./pages/Pricing");
const About = lazyRetry(() => import("./pages/About"), "./pages/About");
const Contact = lazyRetry(() => import("./pages/Contact"), "./pages/Contact");
const Cart = lazyRetry(() => import("./pages/Cart"), "./pages/Cart");
const Booking = lazyRetry(() => import("./pages/Booking"), "./pages/Booking");
const BookingSuccess = lazyRetry(() => import("./pages/BookingSuccess"), "./pages/BookingSuccess");
const ClientGalleries = lazyRetry(() => import("./pages/ClientGalleries"), "./pages/ClientGalleries");
const Auth = lazyRetry(() => import("./pages/Auth"), "./pages/Auth");
const AdminLayout = lazyRetry(() => import("./pages/admin/AdminLayout"), "./pages/admin/AdminLayout");
const AdminDashboard = lazyRetry(() => import("./pages/admin/AdminDashboard"), "./pages/admin/AdminDashboard");
const AdminBookings = lazyRetry(() => import("./pages/admin/AdminBookings"), "./pages/admin/AdminBookings");
const AdminGalleries = lazyRetry(() => import("./pages/admin/AdminGalleries"), "./pages/admin/AdminGalleries");
const AdminVendors = lazyRetry(() => import("./pages/admin/AdminVendors"), "./pages/admin/AdminVendors");
const AdminBlog = lazyRetry(() => import("./pages/admin/AdminBlog"), "./pages/admin/AdminBlog");
const AdminTestimonials = lazyRetry(() => import("./pages/admin/AdminTestimonials"), "./pages/admin/AdminTestimonials");
const AdminSubscribers = lazyRetry(() => import("./pages/admin/AdminSubscribers"), "./pages/admin/AdminSubscribers");
const AdminContracts = lazyRetry(() => import("./pages/admin/AdminContracts"), "./pages/admin/AdminContracts");
const AdminAuditLogs = lazyRetry(() => import("./pages/admin/AdminAuditLogs"), "./pages/admin/AdminAuditLogs");
const AdminOrders = lazyRetry(() => import("./pages/admin/AdminOrders"), "./pages/admin/AdminOrders");
const AdminPrintful = lazyRetry(() => import("./pages/admin/AdminPrintful"), "./pages/admin/AdminPrintful");
const AdminContacts = lazyRetry(() => import("./pages/admin/AdminContacts"), "./pages/admin/AdminContacts");
const AdminInvoices = lazyRetry(() => import("./pages/admin/AdminInvoices"), "./pages/admin/AdminInvoices");
const AdminCoupons = lazyRetry(() => import("./pages/admin/AdminCoupons"), "./pages/admin/AdminCoupons");
const AdminSchedule = lazyRetry(() => import("./pages/admin/AdminSchedule"), "./pages/admin/AdminSchedule");
const AdminCalendar = lazyRetry(() => import("./pages/admin/AdminCalendar"), "./pages/admin/AdminCalendar");
const AdminAnalytics = lazyRetry(() => import("./pages/admin/AdminAnalytics"), "./pages/admin/AdminAnalytics");
const AdminEmailMarketing = lazyRetry(() => import("./pages/admin/AdminEmailMarketing"), "./pages/admin/AdminEmailMarketing");
const AdminEmailPreview = lazyRetry(() => import("./pages/admin/AdminEmailPreview"), "./pages/admin/AdminEmailPreview");
const AdminSocialMedia = lazyRetry(() => import("./pages/admin/AdminSocialMedia"), "./pages/admin/AdminSocialMedia");
const AdminImageAudit = lazyRetry(() => import("./pages/admin/AdminImageAudit"), "./pages/admin/AdminImageAudit");
const ContractSign = lazyRetry(() => import("./pages/ContractSign"), "./pages/ContractSign");
const ProductDetail = lazyRetry(() => import("./pages/ProductDetail"), "./pages/ProductDetail");
const NotFound = lazyRetry(() => import("./pages/NotFound"), "./pages/NotFound");
const Terms = lazyRetry(() => import("./pages/Terms"), "./pages/Terms");
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"), "./pages/PrivacyPolicy");
const FAQ = lazyRetry(() => import("./pages/FAQ"), "./pages/FAQ");
const RevealExperience = lazyRetry(() => import("./pages/RevealExperience"), "./pages/RevealExperience");
const ConnectOnboarding = lazyRetry(() => import("./pages/connect/ConnectOnboarding"), "./pages/connect/ConnectOnboarding");
const ConnectProducts = lazyRetry(() => import("./pages/connect/ConnectProducts"), "./pages/connect/ConnectProducts");
const ConnectStorefront = lazyRetry(() => import("./pages/connect/ConnectStorefront"), "./pages/connect/ConnectStorefront");
const ConnectSuccess = lazyRetry(() => import("./pages/connect/ConnectSuccess"), "./pages/connect/ConnectSuccess");
const ConnectDashboard = lazyRetry(() => import("./pages/connect/ConnectDashboard"), "./pages/connect/ConnectDashboard");
const ConnectAccountStorefront = lazyRetry(() => import("./pages/connect/ConnectAccountStorefront"), "./pages/connect/ConnectAccountStorefront");
const BusinessCardDesign = lazyRetry(() => import("./pages/BusinessCardDesign"), "./pages/BusinessCardDesign");
const FlyerDesign = lazyRetry(() => import("./pages/FlyerDesign"), "./pages/FlyerDesign");
const PrintShop = lazyRetry(() => import("./pages/PrintShop"), "./pages/PrintShop");
const PrintProductDetail = lazyRetry(() => import("./pages/PrintProductDetail"), "./pages/PrintProductDetail");
const OklahomaPhotographer = lazyRetry(() => import("./pages/OklahomaPhotographer"), "./pages/OklahomaPhotographer");
const SessionPrepGuide = lazyRetry(() => import("./pages/SessionPrepGuide"), "./pages/SessionPrepGuide");
const GiveawayThankYou = lazyRetry(() => import("./pages/GiveawayThankYou"), "./pages/GiveawayThankYou");
const Unsubscribe = lazyRetry(() => import("./pages/Unsubscribe"), "./pages/Unsubscribe");
const OAuthConsent = lazyRetry(() => import("./pages/OAuthConsent"), "./pages/OAuthConsent");

const queryClient = new QueryClient();

const PageFallback = () => <div className="min-h-screen bg-background" />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/prints" element={<PrintShop />} />
                <Route path="/prints/:productId" element={<PrintProductDetail />} />
                <Route path="/product/:handle" element={<ProductDetail />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/booking/success" element={<BookingSuccess />} />
                <Route path="/galleries" element={<ClientGalleries />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="calendar" element={<AdminCalendar />} />
                  <Route path="bookings" element={<AdminBookings />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="invoices" element={<AdminInvoices />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="printful" element={<AdminPrintful />} />
                  <Route path="contracts" element={<AdminContracts />} />
                  <Route path="coupons" element={<AdminCoupons />} />
                  <Route path="galleries" element={<AdminGalleries />} />
                  <Route path="vendors" element={<AdminVendors />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="subscribers" element={<AdminSubscribers />} />
                  <Route path="schedule" element={<AdminSchedule />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="email-marketing" element={<AdminEmailMarketing />} />
                  <Route path="email-preview" element={<AdminEmailPreview />} />
                  <Route path="social-media" element={<AdminSocialMedia />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                  <Route path="image-audit" element={<AdminImageAudit />} />
                </Route>
                <Route path="/contract/sign/:contractId" element={<ContractSign />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/reveal-experience" element={<RevealExperience />} />
                <Route path="/connect/onboarding" element={<ConnectOnboarding />} />
                <Route path="/connect/products" element={<ConnectProducts />} />
                <Route path="/connect/storefront" element={<ConnectStorefront />} />
                <Route path="/connect/storefront/:accountId" element={<ConnectAccountStorefront />} />
                <Route path="/connect/success" element={<ConnectSuccess />} />
                <Route path="/connect/dashboard" element={<ConnectDashboard />} />
                <Route path="/business-card" element={<BusinessCardDesign />} />
                <Route path="/flyer" element={<FlyerDesign />} />
                <Route path="/oklahoma-photographer" element={<OklahomaPhotographer />} />
                <Route path="/portland-photographer" element={<Navigate to="/oklahoma-photographer" replace />} />
                <Route path="/session-prep-guide" element={<SessionPrepGuide />} />
                <Route path="/giveaway-thank-you" element={<GiveawayThankYou />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;